import { Box, Tab, Tabs } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ReactElement, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';

import { smaverseState } from '@app/domain/top-nav';
import { useDeviceType } from '@app/hooks/useBrowserHooks';
import { CategoryName } from '@app/types/catalog';

const tabs = [
  { label: '住宅展示場を探す', value: CategoryName.EXHIBITION },
  { label: '分譲住宅を探す', value: CategoryName.PROPERTY },
] as const;

interface SearchTypeTabProps {
  onChangeTab: (selectedTab: string) => void;
  value?: string | null;
}

export function SearchTypeTab({
  onChangeTab,
  value,
}: SearchTypeTabProps): ReactElement {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDesktop } = useDeviceType();
  const setIsSmaverse = useSetRecoilState(smaverseState);
  const selected = useMemo(() => {
    if (location.pathname.includes('/exhibitions')) {
      return CategoryName.EXHIBITION;
    } else if (location.pathname.includes('/properties')) {
      return CategoryName.PROPERTY;
    }
    return tabs[0].value;
  }, [location.pathname]);

  const handleChange = useCallback(
    (event: React.SyntheticEvent, selectedTab: string) => {
      onChangeTab(selectedTab);
      setIsSmaverse(false);
      const params = new URLSearchParams(location.search);
      const locationsParam = params.get('locations');
      const newParams = new URLSearchParams();
      locationsParam && newParams.set('locations', locationsParam);

      // NOTE: 利便性のため分譲と展示場で共通の建築エリア以外の検索条件も保持する。
      if (selectedTab === CategoryName.EXHIBITION) {
        navigate(`/exhibitions?${newParams.toString()}`);
      } else if (selectedTab === CategoryName.PROPERTY) {
        navigate(`/properties?${newParams.toString()}`);
      }
    },
    [onChangeTab, setIsSmaverse, location.search, navigate]
  );

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.neutral.grey1,
        display: 'flex',
        justifyContent: 'center',
        py: isDesktop ? 3 : 1,
      }}
    >
      <Tabs
        value={selected}
        onChange={handleChange}
        centered
        TabIndicatorProps={{
          style: {
            backgroundColor: theme.palette.primary.main,
            border: 'none',
            display: 'none',
          },
        }}
        sx={{
          bgcolor: 'white',
          borderRadius: 15,
          width: 'auto',
        }}
      >
        {tabs.map(({ label, value }, index) => (
          <Tab
            key={index}
            label={label}
            value={value}
            sx={{
              '&.Mui-selected': {
                backgroundColor: theme.palette.blueAccent.main,
                border: 'solid 1px',
                borderColor: theme.palette.blueAccent.main,
                color: 'white',
              },
              borderRadius: 15,
              color: '#A0A0A0',
              fontSize: '16px',
              fontWeight: 'bold',
              py: isDesktop ? 3 : 0,
              width: isDesktop ? '27vw' : '40vw',
            }}
          />
        ))}
      </Tabs>
    </Box>
  );
}
